// Painel lateral (diálogo) e toast, compartilhados por todas as telas.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

interface PanelState {
  title: string;
  body: ReactNode;
  onClose?: () => void;
  bare?: boolean;
}

export interface PanelOptions {
  /** Sem cabeçalho genérico: o conteúdo é o painel inteiro (ex.: assistente). */
  bare?: boolean;
}

interface OverlayValue {
  /** `onClose` roda quando a pessoa fecha o painel (×, Esc, clique fora ou `closePanel`). */
  openPanel: (title: string, body: ReactNode, onClose?: () => void, opts?: PanelOptions) => void;
  closePanel: () => void;
  /** Fecha sem chamar o `onClose` (ex.: a tela que abriu o painel saiu de cena). */
  dismissPanel: () => void;
  toast: (message: string) => void;
}

const Ctx = createContext<OverlayValue | null>(null);

export function OverlayProvider({ children }: { children: ReactNode }) {
  const [panel, setPanel] = useState<PanelState | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<number>();
  const current = useRef<PanelState | null>(null);
  current.current = panel;

  const closePanel = useCallback(() => {
    const onClose = current.current?.onClose;
    setPanel(null);
    onClose?.();
  }, []);
  const value = useMemo<OverlayValue>(
    () => ({
      openPanel: (title, body, onClose, opts) => setPanel({ title, body, onClose, bare: opts?.bare }),
      closePanel,
      dismissPanel: () => setPanel(null),
      toast: (m) => {
        setMessage(m);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setMessage(null), 3200);
      },
    }),
    [closePanel],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {panel && (
        <Panel title={panel.title} bare={panel.bare} onClose={closePanel}>
          {panel.body}
        </Panel>
      )}
      <div id="toast" role="status" style={{ display: message ? "block" : "none" }}>
        {message}
      </div>
    </Ctx.Provider>
  );
}

function Panel(props: { title: string; bare?: boolean; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;
  // Foco inicial só quando o painel muda de título (abriu outro painel), não a cada render.
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>(".panelhead ~ * button, .panelhead ~ * input, .panelhead ~ * select, .panelhead ~ * textarea, button")?.focus();
  }, [props.title]);
  useEffect(() => {
    const el = ref.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
      if (e.key !== "Tab" || !el) return;
      // Prende o foco dentro do painel.
      const f = [...el.querySelectorAll<HTMLElement>("button,input,select,textarea,a,summary")].filter(
        (x) => !(x as HTMLButtonElement).disabled && x.getClientRects().length,
      );
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  if (props.bare) {
    // O conteúdo traz o próprio `.panel`; a section só segura o diálogo, o Esc e o foco.
    return (
      <div id="overlay">
        <div className="shade assistant-scrim" onClick={props.onClose} />
        <section ref={ref} role="dialog" aria-modal="true" aria-label={props.title} style={{ display: "contents" }}>
          {props.children}
        </section>
      </div>
    );
  }
  return (
    <div id="overlay">
      <div className="shade" onClick={props.onClose} />
      <section ref={ref} className="panel" role="dialog" aria-modal="true" aria-label={props.title}>
        <div className="panelhead">
          <h2>{props.title}</h2>
          <button onClick={props.onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        {props.children}
      </section>
    </div>
  );
}

export function useOverlay(): OverlayValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useOverlay fora do OverlayProvider");
  return v;
}
